import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { usePersona } from '../contexts/PersonaContext';
import { useAuth } from '../contexts/AuthContext';
import { Check, Lock } from 'lucide-react';

const PersonaModal = ({ onClose }) => {
  const { selectPersonas, selectedPersonas, personas, showPersonaModal, closePersonaModal } = usePersona();
  const { isAuthenticated, user } = useAuth();
  const [tempSelected, setTempSelected] = useState([...selectedPersonas]);

  const togglePersona = (personaId) => {
    if (tempSelected.includes(personaId)) {
      setTempSelected(tempSelected.filter(id => id !== personaId));
    } else {
      setTempSelected([...tempSelected, personaId]);
    }
  };

  const handleConfirm = () => {
    // Check authentication first
    if (!isAuthenticated || !user) {
      localStorage.setItem('redirect_after_auth', '/session/new');
      closePersonaModal();
      window.location.href = '/auth';
      return;
    }

    if (tempSelected.length === 0) {
      // Require at least one persona
      return;
    }
    selectPersonas(tempSelected, true);
    closePersonaModal();
    
    // Navigate to new session
    window.location.href = '/session/new';
    
    if (onClose) onClose();
  };

  const isSelected = (personaId) => tempSelected.includes(personaId);

  return (
    <Dialog open={showPersonaModal} onOpenChange={closePersonaModal}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
            Choose Your Persona(s)
          </DialogTitle>
          <DialogDescription className="text-base">
            Select one or more roles that describe your thinking context. You can blend multiple perspectives for richer ideation!
          </DialogDescription>
          {!isAuthenticated && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                You'll be asked to sign up or log in before starting your session
              </p>
            </div>
          )}
        </DialogHeader>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {personas.map((persona) => (
            <Card 
              key={persona.id}
              className={`cursor-pointer transition-all duration-300 hover:-translate-y-1 border-2 group relative ${
                isSelected(persona.id) 
                  ? 'border-purple-500 shadow-xl bg-purple-50' 
                  : 'border-slate-200 hover:border-purple-300 hover:shadow-lg'
              }`}
              onClick={() => togglePersona(persona.id)}
            >
              {isSelected(persona.id) && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
              
              <CardHeader>
                <div className={`w-16 h-16 bg-gradient-to-br ${persona.color} rounded-xl flex items-center justify-center mb-3 text-4xl group-hover:scale-110 transition-transform duration-300`}>
                  {persona.icon}
                </div>
                <CardTitle className={`text-xl transition-colors ${
                  isSelected(persona.id) ? 'text-purple-700' : 'group-hover:text-purple-700'
                }`}>
                  {persona.name}
                </CardTitle>
                <CardDescription className="text-sm">{persona.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-slate-600">
                  <strong>Focus Metrics:</strong>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {persona.metrics.map((metric, idx) => (
                      <span key={idx} className={`px-2 py-1 rounded-full text-xs ${
                        isSelected(persona.id) 
                          ? 'bg-purple-200 text-purple-800' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          <div className="text-center text-sm text-slate-600">
            <p className="font-medium text-slate-800 mb-1">
              Selected: {tempSelected.length} persona{tempSelected.length !== 1 ? 's' : ''}
            </p>
            <p>Click on cards to select/deselect. You can change your personas anytime in settings.</p>
          </div>
          
          <div className="flex justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => setTempSelected([])}
              disabled={tempSelected.length === 0}
            >
              Clear All
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={tempSelected.length === 0}
              className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
            >
              Confirm Selection ({tempSelected.length})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonaModal;